import { registrarErro, tratarErro } from '@/lib/erros';

/**
 * ★★★ WU-2 (16/out/2026) — O LISTENER QUE NÃO ACEITA FICAR NO CACHE PARA SEMPRE.
 *
 * ## ⚠⚠⚠ O que foi MEDIDO no passo 0, e é a razão deste arquivo existir
 *
 * Experimento no emulador, com o SDK 12.17.0 (`docs/wu2-testes.md` §1):
 *
 * | caso | o que foi medido |
 * |---|---|
 * | ★★★ `permission-denied` **antes da 1ª resposta do servidor** | o callback de erro é chamado **uma vez** e o listener **MORRE**: escrevi o documento depois e chegaram **0** eventos |
 * | ★★★ `permission-denied` **depois** de já ter dado | idem — e **nem voltar a logar** o ressuscita: **0** eventos novos no MESMO listener |
 * | ★★ queda de rede (`unavailable`) | ⚠ **NÃO chega ao callback de erro**: o SDK emite `next` com `fromCache: true`, tenta sozinho e **se recupera** (medido: 7 s depois da rede voltar) |
 *
 * > ### ★★★ A consequência, em uma frase
 * > ⚠⚠⚠ **Um erro permanente antes da primeira resposta do servidor congela a tela na
 * > emissão do cache — para sempre, sem aviso e sem ninguém tentando de novo.** ★ É a classe
 * > do caso de 16/out (`docs/wu-reincidencia-1610.md`), em que a meta do mês estava no
 * > servidor desde 14/10 e a tela dizia "sem meta" 48 h depois.
 *
 * ## O que este envelope faz, e o que ele NÃO faz
 *
 * | | |
 * |---|---|
 * | ★★★ **P7** | erro no callback → **reinscreve sozinho**, com espera crescente (2 s, 5 s, 15 s, 60 s, teto), registrando **cada** tentativa |
 * | ★★★ **P3** | 1ª emissão `fromCache` → **UMA** leitura do servidor, e o que ela trouxer **substitui** o retrato local |
 * | ★★ **P3** | servidor inalcançável, ou emissão de cache depois de já ter havido servidor → `aoDesatualizar(true)`, que a tela mostra como *"dados podem estar desatualizados"* |
 * | ⚠ **NÃO** limpa o IndexedDB | ★★★ é a lição de 07/out: `clearIndexedDbPersistence` **encerra o cliente das outras abas**. ★ A direção é **não depender** do cache para o dado pequeno, não limpá-lo mais vezes |
 * | ⚠ **NÃO** é para coleção grande | ★ `leads` (8.387 docs) e `mensagens` seguem sem confirmação forçada: ali o cache é o que faz a tela abrir |
 */

/** ★ As esperas da reinscrição, em ms. O último valor é o TETO — repete dali em diante. */
export const ESPERAS_REINSCRICAO_MS = [2_000, 5_000, 15_000, 60_000];

/**
 * @param {object} p
 * @param {string} p.assunto         'as metas', 'a equipe' — vai no registro (P1) e no aviso
 * @param {Function} p.assinar       `(aoSnap, aoErro) => cancelar`; `aoSnap(valor, fromCache)`
 * @param {Function} [p.doServidor]  `() => Promise<valor>` — a leitura forçada do servidor
 * @param {Function} p.aoReceber     recebe o valor (do cache, do servidor ou do listener)
 * @param {Function} [p.aoFalhar]    o tratador da tela, como antes
 * @param {Function} [p.aoDesatualizar] `(boolean)` — ★ chamado nos dois sentidos, para o aviso sumir sozinho
 * @returns {Function} cancela o listener E a reinscrição pendente
 */
export function ouvirVigiado({
  assunto,
  assinar,
  doServidor = null,
  aoReceber,
  aoFalhar = null,
  aoDesatualizar = null,
  esperas = ESPERAS_REINSCRICAO_MS,
  agendar = (fn, ms) => setTimeout(fn, ms),
  desagendar = (id) => clearTimeout(id),
  registrar = registrarErro,
  tratar = tratarErro,
}) {
  let vivo = true;
  let cancelarAssinatura = null;
  let temporizador = null;
  let tentativas = 0;
  /** ★ O servidor já respondeu alguma vez nesta assinatura? */
  let confirmado = false;
  /** ★ A leitura forçada já foi pedida? ⚠ UMA por assinatura — é o que o dono especificou. */
  let pediuServidor = false;

  /** ⚠ Tratador da tela que estoura não pode virar erro não tratado aqui (regra da WT). */
  const avisar = (valor) => {
    try {
      aoDesatualizar?.(valor);
    } catch (e) {
      console.error('[OdontoSDR] aviso de desatualizado estourou:', e);
    }
  };
  const entregar = (valor) => {
    try {
      aoReceber?.(valor);
    } catch (e) {
      console.error('[OdontoSDR] tratador da tela estourou:', e);
    }
  };

  /**
   * ★★★ P3 — A LEITURA FORÇADA DO SERVIDOR.
   *
   * ⚠⚠ Ela é **descartada** se um snapshot do servidor chegar primeiro: ★ o snapshot é mais
   * novo que ela por construção, e aplicar a leitura depois faria a tela **andar para trás**.
   */
  function confirmarComServidor() {
    if (!doServidor || pediuServidor) return;
    pediuServidor = true;
    Promise.resolve()
      .then(() => doServidor())
      .then((valor) => {
        if (!vivo || confirmado) return;
        confirmado = true;
        entregar(valor);
        avisar(false);
      })
      .catch((erro) => {
        if (!vivo) return;
        /**
         * ⚠⚠ Registra, mas **não** chama `aoFalhar`: o listener continua vivo e o dado da tela
         * continua servindo. ★ O que a pessoa precisa saber é que ele **pode estar velho** — e
         * isso é o `aoDesatualizar`, não o "não foi possível carregar".
         */
        registrar({
          origem: 'service',
          codigo: `confirmacao:${erro?.code ?? 'sem-code'}`,
          assunto,
          mensagem: erro?.message ?? String(erro ?? ''),
        });
        avisar(true);
      });
  }

  /**
   * ★★★ P7 — A REINSCRIÇÃO.
   *
   * ⚠ O `codigo` leva o NÚMERO da tentativa de propósito: ★ o dedup de 60 s de
   * `registrarErro` agrupa por `origem|codigo|assunto|tela`, então sem o número a 2ª e a 3ª
   * tentativas dentro do minuto **não apareceriam** — e "registra cada tentativa" viraria
   * "registra a primeira".
   */
  function reinscrever(erro) {
    try {
      cancelarAssinatura?.();
    } catch { /* ⚠ cancelar um listener já morto não pode derrubar a reinscrição */ }
    cancelarAssinatura = null;

    const espera = esperas[Math.min(tentativas, esperas.length - 1)];
    tentativas += 1;
    registrar({
      origem: 'service',
      codigo: `reinscricao:${tentativas}`,
      assunto,
      mensagem: `listener encerrado por ${erro?.code ?? 'erro sem code'}; reinscrevendo em ${espera} ms`,
    });
    temporizador = agendar(() => {
      temporizador = null;
      if (vivo) abrir();
    }, espera);
  }

  function abrir() {
    try {
      cancelarAssinatura = assinar(
        (valor, fromCache) => {
          if (!vivo) return;
          // ★ Uma emissão boa zera a espera: a próxima queda recomeça em 2 s, não em 60.
          tentativas = 0;
          if (fromCache) {
            /**
             * ★★ Duas situações bem diferentes no mesmo `fromCache: true`:
             *  - ⚠⚠ **já houve servidor** → a conexão caiu agora. Medido: nenhum erro chega ao
             *    callback nesse caso, então **este é o único sinal** de que o dado pode estar
             *    velho;
             *  - ★★★ **nunca houve servidor** → é o caso de 16/out. Força UMA leitura.
             */
            if (confirmado) avisar(true);
            else confirmarComServidor();
          } else {
            confirmado = true;
            avisar(false);
          }
          entregar(valor);
        },
        (erro) => {
          if (!vivo) return;
          // ★ Registra (com assunto, P1) e avisa a tela — exatamente como antes.
          tratar(assunto, aoFalhar)(erro);
          // ★★★ E agora tenta de novo: medido que o listener está morto neste ponto.
          reinscrever(erro);
        },
      );
    } catch (erro) {
      // ⚠ `onSnapshot` que estoura na montagem (caminho inválido, cliente encerrado).
      tratar(assunto, aoFalhar)(erro);
      reinscrever(erro);
    }
  }

  abrir();

  return () => {
    vivo = false;
    if (temporizador !== null) desagendar(temporizador);
    temporizador = null;
    try {
      cancelarAssinatura?.();
    } catch { /* idem */ }
  };
}
